import { convertiUtsToDate } from '../../GrapheXT/GrapheXT';
import { EVENT_STYLES, DEFAULT_EVENT_STYLE } from './labelConstants';
import { ICON_EMOJI } from './iaCategories';

export const buildAnnotation = ({
  xDate,
  paperY,
  label,
  dateStr,
  note,
  color,
  valLine = '',
}) => ({
  x: xDate,
  xref: 'x',
  y: paperY,
  yref: 'paper',
  text:
    `<b style="font-size:13px">${label}</b>` +
    `<br><span style="color:#555">Date :</span>  ${dateStr}` +
    valLine +
    (note ? `<br><span style="color:#555">Note :</span>  ${note}` : ''),
  showarrow: true,
  arrowhead: 2,
  arrowsize: 0.8,
  arrowwidth: 2,
  arrowcolor: color,
  ax: 0,
  ay: 36,
  xanchor: 'center',
  yanchor: 'top',
  cliponaxis: false,
  align: 'left',
  font: { size: 12, color: '#1a1a2e', family: 'Montserrat, sans-serif' },
  bgcolor: 'rgba(255,255,255,0.97)',
  bordercolor: color,
  borderwidth: 1.5,
  borderpad: 10,
});

export const buildEventTraces = ({
  items,
  tRaw,
  yRaw,
  donnees,
  yMax,
  tTotal,
  domain,
  yaxisKey,
  markerSize,
  shownLabels,
  graphCurves,
}) => {
  const sorted = [...items]
    .map((ev, i) => ({ ...ev, _i: i }))
    .sort((a, b) => a.t - b.t);

  const PROXIMITY = tTotal * 0.08;
  let groupId = 0;
  const groups = sorted.map((ev, i) => {
    if (i > 0 && Math.abs(ev.t - sorted[i - 1].t) > PROXIMITY) groupId++;
    return groupId;
  });

  const groupCount = {};
  const indexAtX = {};
  const traces = [];
  const annMeta = [];

  sorted.forEach((ev, i) => {
    const iconType = ev.iconType || ev.label;
    const style = EVENT_STYLES[iconType] || DEFAULT_EVENT_STYLE;
    const emoji = ICON_EMOJI[iconType] || ICON_EMOJI.circle;
    const xDate = convertiUtsToDate([ev.t])[0];
    const key = ev.t;
    indexAtX[key] = indexAtX[key] || 0;
    const stackIdx = indexAtX[key];
    indexAtX[key]++;
    const markerY = 0.5 + ((stackIdx % 3) - Math.min(stackIdx, 2) / 2) * 0.3;

    // Recherche la courbe correspondant au capteur/position du label.
    // Stratégie : correspondance exacte → index graphCurves → capteur seul → courbe[0].
    let activeCurve = null;
    if (donnees?.length && ev.idSensor != null) {
      const sid = Number(ev.idSensor);
      const pos = Number(ev.positionInSensor ?? -1);
      activeCurve =
        donnees.find((d) => {
          const dPos = Number(
            d.position_in_sensor ?? d.channel ?? d.position ?? -1
          );
          return Number(d.id_sensor) === sid && dPos === pos;
        }) ?? null;
      if (!activeCurve && graphCurves?.length) {
        const gcIdx = graphCurves.findIndex((gc) => {
          const nums = (gc.formula || '').match(/\d+/g)?.map(Number);
          return nums?.length >= 2 && nums[0] === sid && nums[1] === pos;
        });
        if (gcIdx >= 0 && gcIdx < donnees.length) activeCurve = donnees[gcIdx];
      }
      if (!activeCurve) {
        const bySensor = donnees.filter((d) => Number(d.id_sensor) === sid);
        if (bySensor.length === 1) activeCurve = bySensor[0];
      }
    }
    const rawT = activeCurve ? activeCurve.x || activeCurve.t || tRaw : tRaw;
    const activeT = rawT.length && typeof rawT[0] === 'number' ? rawT : tRaw;
    const activeY = activeCurve ? activeCurve.y || [] : yRaw;

    let yCurve = yMax;
    if (activeT.length > 0) {
      let closest = 0,
        minDiff = Math.abs(activeT[0] - ev.t);
      for (let j = 1; j < activeT.length; j++) {
        const diff = Math.abs(activeT[j] - ev.t);
        if (diff < minDiff) {
          minDiff = diff;
          closest = j;
        }
      }
      yCurve = activeY[closest] ?? yMax;
    }

    const gid = groups[i];
    groupCount[gid] = (groupCount[gid] || 0) + 1;

    const rawLabel = (
      ev.label_text ??
      ev.label ??
      iconType ??
      'Sans label'
    ).trim();
    const displayLabel =
      rawLabel.length > 20 ? `${rawLabel.slice(0, 20)}...` : rawLabel;
    const legendGroup = `category:${iconType}`;
    const showLegend =
      !shownLabels.has(legendGroup) &&
      (() => {
        shownLabels.add(legendGroup);
        return true;
      })();

    const categoryLabel =
      iconType === 'diamond' ? 'Jalon' : iconType === 'circle' ? 'IA' : iconType;
    const legendName = emoji ? `${emoji} ${categoryLabel}` : categoryLabel;
    const isJalon = iconType === 'diamond';
    const iconSize = Math.round(markerSize * 1.5);

    // For IA categories: one empty marker trace as legend entry (no "Aa"), then a text-only trace per point.
    if (!isJalon && showLegend) {
      traces.push({
        type: 'scatter',
        mode: 'markers',
        name: legendName,
        legendgroup: legendGroup,
        showlegend: true,
        xaxis: 'x',
        yaxis: yaxisKey,
        x: [null],
        y: [null],
        hoverinfo: 'none',
        marker: { size: 10, color: style.color, symbol: 'circle', line: { width: 0 } },
      });
    }

    traces.push(
      isJalon
        ? {
            type: 'scatter',
            mode: 'markers',
            name: legendName,
            legendgroup: legendGroup,
            showlegend: showLegend,
            xaxis: 'x',
            yaxis: yaxisKey,
            x: [xDate],
            y: [markerY],
            cliponaxis: false,
            hoverinfo: 'none',
            customdata: [ev.t],
            marker: {
              size: markerSize,
              color: style.color,
              symbol: 'circle',
              line: { color: style.color, width: 2 },
            },
          }
        : {
            type: 'scatter',
            mode: 'text',
            name: legendName,
            legendgroup: legendGroup,
            showlegend: false,
            xaxis: 'x',
            yaxis: yaxisKey,
            x: [xDate],
            y: [markerY],
            cliponaxis: false,
            hoverinfo: 'none',
            customdata: [ev.t],
            text: [emoji],
            textposition: 'middle center',
            textfont: { size: iconSize, color: style.color, family: 'Arial' },
          }
    );

    const dateStr = new Date(ev.t * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const valLine = `<br><span style="color:#555">Valeur :</span>  <b>${Math.round(yCurve * 10) / 10}</b>`;

    annMeta.push({
      xDate,
      t: ev.t,
      markerY,
      style,
      legendGroup,
      label: displayLabel,
      note: ev.note,
      dateStr,
      yCurveVal: yCurve,
      valLine,
      domain,
    });
  });

  return { traces, annMeta };
};
